import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, TableSkeleton } from "@/components/shared";
import JobForm from "@/components/jobs/JobForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Plus, Briefcase, ChevronLeft, ChevronRight } from "lucide-react";

export default function Jobs() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);

  const fetch = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get("/jobs", { params: { q: q || undefined, page, page_size: 15 } });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); setData({ items: [], total: 0, pages: 0 }); }
  }, [q, page]);
  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div>
      <PageHeader title="Jobs" subtitle={data ? `${data.total} requisition${data.total === 1 ? "" : "s"}` : "Requisition management"}
        actions={can("job:create") && <Button size="sm" onClick={() => setFormOpen(true)} data-testid="jobs-create-button" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><Plus className="h-4 w-4" /> New Job</Button>} />
      <Card className="p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="jobs-search" placeholder="Search title, client, req ID…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9" />
        </div>
      </Card>
      <Card className="overflow-hidden">
        {!data ? <div className="p-4"><TableSkeleton /></div>
          : data.items.length === 0 ? <EmptyState icon={Briefcase} title="No jobs yet" description="Create a requisition to start recruiting."
              action={can("job:create") && <Button onClick={() => setFormOpen(true)} className="bg-[#255058] text-white">New Job</Button>} />
            : (
              <div className="overflow-x-auto scrollbar-thin">
                <Table>
                  <TableHeader><TableRow><TableHead>Job</TableHead><TableHead className="hidden md:table-cell">Location</TableHead><TableHead className="hidden lg:table-cell">Type</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead className="hidden lg:table-cell">Days Open</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.items.map((j) => (
                      <TableRow key={j.id} data-testid={`job-row-${j.id}`} className="cursor-pointer hover:bg-accent/40" onClick={() => navigate(`/jobs/${j.id}`)}>
                        <TableCell><div className="font-medium">{j.title}</div><div className="text-xs text-muted-foreground">{j.requisition_id} · {j.client_name}</div></TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{j.location || "—"}</TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">{(j.staffing_type || "").replace(/_/g, " ")}</TableCell>
                        <TableCell><StatusBadge value={j.priority} /></TableCell>
                        <TableCell><StatusBadge value={j.status} /></TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">{j.days_open ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>
      <JobForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={(j) => { setFormOpen(false); fetch(); if (j?.id) navigate(`/jobs/${j.id}`); }} />
    </div>
  );
}
